<template>
  <!-- The server refused a request because of the company it was for: locked or suspended
       (AccountLockedId), inactive and the person only a mechanic there (CompanyNotActiveId),
       or one the person only drives at (NoPortalAccess) - 86e06umb3. One notice,
       however many requests the page had in flight, and a way out instead of a blank page. -->
  <v-dialog :model-value="!!refusal" persistent max-width="460" data-test="company-access-refused">
    <v-card class="pa-2">
      <v-card-title class="text-h6 company-refused-wrap">
        {{ $t('company_access_refused_title') }}
      </v-card-title>
      <v-card-text class="text-body-1 company-refused-wrap" data-test="company-access-refused-message"><template
          v-for="(part, partIndex) in parts" :key="partIndex"><span v-if="part.keep"
            class="company-refused-nowrap">{{ part.text }}</span><template v-else>{{ part.text }}</template></template></v-card-text>
      <v-card-actions class="flex-wrap ga-2">
        <v-btn variant="text" @click="signOut">{{ $t('shell_back_to_sign_in') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" @click="chooseCompany">{{ $t('company_access_refused_choose') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import { t } from '@/i18n/index.js';
import ConstUtils from '@/utils/ConstUtils.js';
import CompanyChoices from '@/v3/pages/dmz/CompanyChoices.js';
import PasswordRecoveryReply from '@/v3/pages/dmz/PasswordRecoveryReply.js';

const store = useStore();
const route = useRoute();
const router = useRouter();

const refusal = ref(null);
const parts = computed(() => PasswordRecoveryReply.messageParts(refusal.value ? refusal.value.message : ''));

watch(() => store.getters.auth_socket_message, (event) => {
  if (refusal.value) return;
  const found = CompanyChoices.guardRefusal(event, route.path, {
    lockedFallback: t('company_select_locked_fallback'),
    noAccessFallback: t('company_access_refused_no_access'),
  });
  if (found) {
    refusal.value = found;
    // The refusal is for the company this page is in: list it as "Contact support" from now on.
    // The list holds the employee's company; the session's may be one of its divisions.
    const domain = store.getters.domain;
    if (domain) {
      const session = domain.session();
      const ids = [session.employee().company().id(), session.company().id()];
      for (const id of ids) {
        CompanyChoices.markRefused(store.getters.signin_event, id, found.messageId);
      }
    }
  }
});

// Leaving the company's pages - by either button, or any other way - closes the notice.
watch(() => route.path, (path) => {
  if (refusal.value && !path.startsWith('/portal/v3/operator/')) {
    refusal.value = null;
  }
});

const chooseCompany = () => {
  refusal.value = null;
  router.replace({ name: ConstUtils.ROUTES.COMPANY.SELECT });
};

const signOut = () => {
  refusal.value = null;
  store.dispatch('authClear');
  router.replace({ name: ConstUtils.ROUTES.SIGNIN });
};
</script>

<style scoped>
.company-refused-wrap {
  white-space: normal;
  overflow-wrap: anywhere;
}

.company-refused-nowrap {
  white-space: nowrap;
}
</style>
